import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import useAuth from "$hooks/useAuth";
import useDockyardForm from "$hooks/useDockyardForm";
import useProjectForm from "$hooks/useProjectForm";
import { useSnackbar } from "$utils/useSnackbar";
import {
  snackbarErrorOptions,
  snackbarSuccessOptions,
} from "$utils/SnackbarOptions";
import { DockyardServices, ProjectServices } from "$services";
import { IDockyard } from "$services/Dockyard.service";
import { ProjectType } from "$types";
import { CacheInvalidationAction } from "$utils/cache";

const PROJECT_DEACTIVATION_BLOCKED_MESSAGE =
  "Ce projet ne peut pas être désactivé car des EDB ou préparations sont en cours.";

const isProjectDeactivationBlockedError = (message?: string | null) =>
  !!message?.includes("Impossible de désactiver ce projet");

export default function useProjectDetailsLogic() {
  const param = useParams<"idProject">();
  const { idProject } = param;
  const { isAdmin, token } = useAuth();
  const queryClient = useQueryClient();
  const [openSnackbar] = useSnackbar(snackbarSuccessOptions);
  const [openSnackbarError] = useSnackbar(snackbarErrorOptions);
  const navigate = useNavigate();
  const { control, setValue } = useForm({
    mode: "onChange",
  });

  const [project, setProject] = useState<ProjectType | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [modalDeleteOpen, setModalDeleteOpen] = useState<boolean>(false);
  const [showImportModal, setShowImportModal] = useState<boolean>(false);
  const [showImportEditModal, setShowImportEditModal] =
    useState<boolean>(false);
  const [modalActive, setModalActive] = useState<boolean>(false);
  const [isAlias, setIsAlias] = useState<boolean>(false);
  const [cleanEmail, setCleanEmail] = useState<string>("");
  const [contact, setContact] = useState<any>(null);
  const [errorUpload, setErrorUpload] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<any>({
    medias: (project?.medias || []).map((media: any) => media || {}),
  });

  const canEdit = isAdmin();
  const isProjectDisabled = project?.isDisable || project?.companyDisable;

  const fetchProject = useCallback(async () => {
    ProjectServices.GetProject(token, idProject || "").then((pr: any) => {
      setProject(pr);
      setActive(!pr?.isDisable && !pr?.companyDisable);
      setContact(pr?.contact);

      let result = false;
      const email = (pr?.contact as any)?.email;
      const regex = /\+\d{13}@/;
      if (email) {
        result = regex.test(email);
      }
      if (result) {
        const modifiedEmail = email.replace(regex, "@");
        setCleanEmail(modifiedEmail);
        setIsAlias(true);
      } else {
        setIsAlias(false);
      }
    });
  }, [idProject, setActive, token]);

  const {
    openPopin: openPopinDockyard,
    togglePopin: toggleDock,
    handleSubmit: handleSubmitDock,
    isOpened: isDockyardPopinOpened,
    error: errorDockyard,
    submitting: submittingDockyard,
    dockyard: formDockyard,
  } = useDockyardForm(fetchProject);
  const {
    togglePopin,
    openPopin: openPopinProject,
    isOpened,
    submitting,
    handleSubmit,
    error,
    project: formProject,
  } = useProjectForm(fetchProject);

  const handleSwitch = () => {
    setActive((prev) => !prev);
  };

  const onImportFinish = async () => {};

  const handleEditProject = async () => {
    if (project) {
      await openPopinProject(project._id);
    }
  };

  const handleSwitchDock = (d: IDockyard) => {
    if (d && d._id && localStorage) {
      localStorage.setItem(d._id, JSON.stringify(d));
      navigate(`/projets/${idProject}/${d._id}`);
    }
  };

  const handleSwitchToggle = async () => {
    setModalActive(true);
  };

  const deleteProject = async () => {
    await ProjectServices.DeleteProject(token, project._id);
    navigate("/projets");
  };

  const disableProject = async () => {
    setIsLoading(true);
    try {
      const result = await ProjectServices.DisabledProject(
        token,
        idProject || ""
      );

      if (result?.error) {
        openSnackbarError(
          isProjectDeactivationBlockedError(result.error)
            ? PROJECT_DEACTIVATION_BLOCKED_MESSAGE
            : result.error
        );
        return;
      }

      setModalActive(false);
      await fetchProject();
      await queryClient.invalidateQueries({
        queryKey: [CacheInvalidationAction.GET_DOCKYARDS],
      });
      await queryClient.invalidateQueries({
        queryKey: [CacheInvalidationAction.GET_EDBS],
      });
      setActive(false);
      setErrorMessage(null);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Une erreur est survenue lors de la désactivation du projet";
      openSnackbarError(
        isProjectDeactivationBlockedError(message)
          ? PROJECT_DEACTIVATION_BLOCKED_MESSAGE
          : message
      );
    } finally {
      setModalActive(false);
      setIsLoading(false);
    }
  };

  const enableProject = async () => {
    setIsLoading(true);
    try {
      await ProjectServices.EnableProject(token, idProject || "");
      setModalActive(false);
      await fetchProject();
      await queryClient.invalidateQueries({
        queryKey: [CacheInvalidationAction.GET_DOCKYARDS],
      });
      await queryClient.invalidateQueries({
        queryKey: [CacheInvalidationAction.GET_EDBS],
      });
      setActive(true);
      setErrorMessage(null);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Une erreur est survenue lors de l'activation du projet";
      setErrorMessage(message);
      setModalActive(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseError = () => {
    setErrorMessage(null);
  };

  useEffect(() => {
    setFormValues({
      medias: (project?.medias || []).map((media: any) => media || {}),
    });
  }, [project]);

  useEffect(() => {
    setValue("medias", formValues.medias, { shouldValidate: false });
  }, [formValues.medias, setValue, formValues]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject, idProject]);

  const filters = useMemo(
    () => ({
      projectId: idProject,
      companyDisable: false,
      ...(active
        ? { isActive: true, projectDisable: false }
        : { isActive: false }),
    }),
    [idProject, active]
  );

  const { data: dockyards } = useQuery({
    queryKey: [CacheInvalidationAction.GET_DOCKYARDS, filters],
    queryFn: () => DockyardServices.getDockyards(filters),
    enabled: !!idProject,
    staleTime: 1000 * 60,
  });

  const dockyardTableData = useMemo(
    () =>
      (dockyards?.data ?? []).filter((d: any) =>
        active ? !d.isDisable && !d?.projectDisable && !d?.companyDisable : true
      ),
    [dockyards, active]
  );

  const email = contact?.email;
  const firstname = contact?.firstname ?? "";
  const lastname = contact?.lastname ?? "";
  const fullname =
    firstname.length > 0 && lastname.length > 0
      ? `${firstname} ${lastname.toUpperCase()}`
      : "N/A";

  return {
    idProject,
    project,
    active,
    canEdit,
    isProjectDisabled,
    isAdminUser: isAdmin(),
    isLoading,

    modalDeleteOpen,
    setModalDeleteOpen,
    showImportModal,
    setShowImportModal,
    showImportEditModal,
    setShowImportEditModal,
    modalActive,
    setModalActive,
    errorUpload,
    setErrorUpload,
    errorMessage,
    handleCloseError,

    isAlias,
    cleanEmail,
    contact,
    email,
    fullname,

    control,

    filters,
    dockyards,
    dockyardTableData,

    handleSwitch,
    handleSwitchToggle,
    handleEditProject,
    handleSwitchDock,
    onImportFinish,
    deleteProject,
    disableProject,
    enableProject,

    openPopinDockyard,
    toggleDock,
    isDockyardPopinOpened,
    errorDockyard,
    submittingDockyard,
    handleSubmitDock,
    formDockyard,

    togglePopin,
    openPopinProject,
    isOpened,
    submitting,
    handleSubmit,
    error,
    formProject,

    openSnackbar,
  };
}
